import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useT } from '../i18n';
import ChatList, { otherParticipant } from '../components/ChatList';
import ChatThread from '../components/ChatThread';
import { useChats, useOpenChat } from '../hooks/useChat';
import {
  hasSupportChat, supportIdentity, SUPPORT_ADMIN_UID_FALLBACK, SUPPORT_DISPLAY_NAME,
} from '../constants/support';
import { getSupportAdminUid } from '../firebase/supportService';
import '../pages/Messages.css';
import './ChatUIContext.css';

const Ctx = createContext({ openMessages: () => {}, close: () => {}, messagesOpen: false });

/** Anything that wants to show messages calls this. */
export function useChatUI() {
  return useContext(Ctx);
}

// ── CHAT OVERLAY ─────────────────────────────────────────────────────────────
// Messages open OVER the page rather than replacing it: writing to a designer
// should not cost you the profile you were reading, and a chat you opened from
// a photo grid should hand that grid back when you close it.
//
// Mounted ONCE, at the router root, for two reasons. Rendering it per page
// would put a second copy — and a second listener — on screen whenever two
// entry points existed; and the listeners inside only exist while the overlay
// is open, because the components holding them unmount with it.
function ChatOverlay({ initialChatId, openSupport, onClose }) {
  const { user } = useAuth();
  const { t } = useT();
  const { chats } = useChats();
  const [selectedId, setSelectedId] = useState(initialChatId || '');
  // Phone shows one pane at a time; a chat chosen on the way in starts on the
  // thread, otherwise on the list.
  const [showThread, setShowThread] = useState(!!initialChatId);

  // Opening the overlay again with a different conversation must switch to it.
  useEffect(() => {
    if (initialChatId) { setSelectedId(initialChatId); setShowThread(true); }
  }, [initialChatId]);

  // ── THE OPEN CONVERSATION WAS DELETED ─────────────────────────────────────
  // Removing a chat from the list (see hideChat) drops it from the live
  // listener, but `selectedId` went on pointing at it. On a desktop that left
  // an empty thread beside a list you could pick from; ON A PHONE the list is
  // hidden while a thread is showing, so the whole overlay became the words
  // "choose a conversation" with nothing to choose from — and no way back
  // except leaving the page entirely.
  //
  // ONLY FOR A CHAT THAT WAS ACTUALLY IN THE LIST. "Not listed" is also true of
  // a conversation being created right now — the Message button on a profile
  // opens the thread before the document exists — so absence alone would throw
  // you out of every brand-new chat the moment you opened one. `listed` is what
  // separates the two: gone means it was there and no longer is.
  //
  // IT TRACKS ONE ID, NOT A SET OF THEM, and that distinction is load-bearing.
  // A chat id is its two uids, so deleting a conversation and then messaging
  // that person again produces the SAME id — and a remembered "this one was
  // listed" from the previous conversation would bounce the reader straight
  // out of the new one, which has not been listed yet. Re-selecting an id
  // starts it fresh.
  const seen = useRef({ id: '', listed: false });
  useEffect(() => {
    // Nothing selected: forget what was. Without this the flag outlives the
    // conversation it described, and re-opening the same id — which is what
    // messaging that person again does — reads the OLD "was listed" and clears
    // the selection the moment it is made.
    if (!selectedId) { seen.current = { id: '', listed: false }; return; }
    if (seen.current.id !== selectedId) seen.current = { id: selectedId, listed: false };
    if (chats.some(c => c.id === selectedId)) { seen.current.listed = true; return; }
    if (!seen.current.listed) return;                // never listed — still being made
    seen.current = { id: '', listed: false };
    setSelectedId('');
    setShowThread(false);                            // back to the list on a phone
  }, [chats, selectedId]);

  // Escape closes, like every other overlay in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── keeping the page still behind the modal ───────────────────────────────
  //
  // `overflow: hidden` on the body is enough on desktop and NOT enough on iOS.
  // When the keyboard opens, Safari scrolls the DOCUMENT to bring the focused
  // input into view; overflow:hidden does not stop it, and because the overlay
  // is position:fixed against the LAYOUT viewport it stays put while the page
  // slides underneath. That is the drift you see.
  //
  // The fix is to make the document unscrollable in a way iOS respects: pin the
  // body with position:fixed at its current offset, then put the scroll back on
  // close. Nothing can scroll, so nothing can drift.
  useEffect(() => {
    const body = document.body;
    // Pinning the body sets the page scroll to 0. If this effect runs twice —
    // React StrictMode double-invokes in development, and any remount does the
    // same — the second run would read that 0 and "restore" the user to the top
    // of the page on close. So when the body is ALREADY pinned, the real
    // position is the offset we parked in `top`, not what the window reports.
    const alreadyPinned = body.style.position === 'fixed';
    const scrollY = alreadyPinned
      ? Math.abs(parseInt(body.style.top || '0', 10)) || 0
      : window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      Object.assign(body.style, prev);
      // Pinning the body threw the scroll position away; put it back, or closing
      // the modal jumps the user to the top of whatever page they were on.
      window.scrollTo(0, scrollY);
    };
  }, []);

  // ── sizing to the VISIBLE viewport ────────────────────────────────────────
  //
  // With the document pinned, the second half of the problem remains: the
  // keyboard covers the bottom of the screen, but `100%`/`100vh` still measure
  // the full window, so the message input ends up underneath it.
  //
  // visualViewport reports what is actually visible. Tracking it keeps the panel
  // sitting exactly in the gap above the keyboard. `offsetTop` matters too —
  // iPad in particular scrolls the visual viewport rather than resizing it.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;                 // older browsers keep the CSS default
    const root = document.documentElement;

    // ── IS THE KEYBOARD UP? ───────────────────────────────────────────────
    //
    // TWO SIGNALS, EITHER OF WHICH IS ENOUGH, because neither is reliable
    // everywhere on its own:
    //
    //   · THE MEASUREMENT. Lost viewport height. 120px is far more than
    //     browser chrome collapsing and far less than any keyboard. Correct on
    //     Android and iOS — but it reports nothing at all under
    //     `interactive-widget=overlays-content`, where the keyboard resizes
    //     neither viewport, and it lags the animation on iOS by a frame or two.
    //
    //   · THE FOCUS. A text field inside the conversation holding focus means
    //     a keyboard, on every platform, with no arithmetic. It is also the
    //     earlier of the two — focus lands before the keyboard finishes
    //     sliding up, so the tab bar goes at the start of the animation rather
    //     than after it.
    //
    // What this drives: the overlay takes the reserved tab-bar strip back, and
    // the bar itself stands down (see .cx-kb in the CSS). On iOS that second
    // part is not cosmetic — Safari visually lifts fixed elements with the
    // keyboard, so the bar parks itself between the composer and the keys and
    // no amount of `bottom: 0` holds it down.
    const typingHere = () => {
      const el = document.activeElement;
      if (!el || !/^(INPUT|TEXTAREA)$/.test(el.tagName)) return false;
      return !!el.closest?.('.cx-overlay');
    };

    const apply = () => {
      root.style.setProperty('--cx-vvh', `${vv.height}px`);
      root.style.setProperty('--cx-vvtop', `${vv.offsetTop}px`);
      root.classList.toggle(
        'cx-kb',
        (window.innerHeight - vv.height > 120) || typingHere(),
      );
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    // focusin/focusout, not focus/blur: those do not bubble, and the field is
    // several levels below this component.
    document.addEventListener('focusin', apply);
    document.addEventListener('focusout', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      document.removeEventListener('focusin', apply);
      document.removeEventListener('focusout', apply);
      root.style.removeProperty('--cx-vvh');
      root.style.removeProperty('--cx-vvtop');
      root.classList.remove('cx-kb');
    };
  }, []);

  // ── OPENING THE SUPPORT CONVERSATION ──────────────────────────────────────
  // openChat is idempotent — the id is the two uids sorted — so pressing this
  // a second time reopens the SAME thread rather than starting a parallel one.
  // That is what makes support a continuous conversation instead of a series
  // of disconnected tickets.
  // Read once when the overlay opens, then cached in the service for five
  // minutes. Falls back to the env var so an existing deployment keeps working.
  const [supportUid, setSupportUid] = useState(SUPPORT_ADMIN_UID_FALLBACK);
  useEffect(() => {
    let alive = true;
    getSupportAdminUid().then((uid) => { if (alive && uid) setSupportUid(uid); });
    return () => { alive = false; };
  }, []);

  const { start: startChatWith, busy: supportBusy } = useOpenChat();
  const openSupportChat = useCallback(async () => {
    const id = await startChatWith(supportIdentity(supportUid, SUPPORT_DISPLAY_NAME));
    if (!id) return;
    setSelectedId(id);
    setShowThread(true);
  }, [startChatWith, supportUid]);

  // ── OPENED STRAIGHT INTO THE ADMIN THREAD ─────────────────────────────────
  // Set when the overlay was opened by something asking for support rather than
  // for the message list — the Contact admin button on /plans. Waits for
  // supportUid, which arrives from a Firestore read a moment after mount, and
  // fires ONCE: startChatWith is idempotent but re-running it would keep
  // yanking the reader back to this thread if they navigated away inside the
  // overlay.
  const jumped = useRef(false);
  useEffect(() => {
    if (!openSupport || jumped.current || !supportUid) return;
    jumped.current = true;
    openSupportChat();
  }, [openSupport, supportUid, openSupportChat]);

  const selected = chats.find(c => c.id === selectedId) || null;

  // THE RECIPIENT COMES FROM THE ID, NOT THE LIST.
  //
  // The id IS the two uids sorted, so the other party can always be recovered
  // from it — no document required. That matters because the list is not a
  // reliable source: a chat just created is not in it yet, and one hidden is
  // filtered out of it on purpose. Either way `other.uid` came back empty, and
  // useChat refuses to send without a recipient — a thread you could read and
  // could not reply in.
  //
  // The name and picture still come from the list when it has them, and are
  // simply absent for the moment it takes to arrive.
  const fromId = selectedId && user?.uid
    ? { uid: selectedId.split('__').find(u => u !== user.uid) || '', name: '', avatar: '' }
    : null;
  const listed = otherParticipant(selected, user?.uid);
  const other = listed?.uid ? listed : fromId;

  return (
    <div className="cx-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className={`cx-panel${showThread ? ' cx-panel--thread' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <header className="cx-head">
          <h2 className="cx-title">{t('chat_title')}</h2>
          {/* Desktop only. On a phone the overlay no longer covers the bottom
              tab bar, so tapping any tab both navigates and closes this — the
              ✕ was a second way to do what the bar already does, in the corner
              furthest from a thumb. */}
          <button type="button" className="cx-close" onClick={onClose} aria-label={t('cancel')}>✕</button>
        </header>

        <div className={`msg-split cx-split${showThread ? ' msg-split--thread' : ''}`}>
          <aside className="msg-list">
            <ChatList
              selectedId={selectedId}
              // AN EMPTY ID IS A DESELECTION, NOT A CHOICE. Deleting the open
              // conversation calls this with '' (see ChatList's confirm), and
              // switching to the thread regardless is what left a phone showing
              // "choose a conversation" with the list hidden behind it — every
              // other conversation still there and unreachable without leaving
              // the page. Picking a chat opens it; clearing the pick goes back
              // to the list, which is the only place a pick can be made.
              onSelect={(id) => { setSelectedId(id); setShowThread(!!id); }}
            />

            {/* ── CONTACT ADMIN ────────────────────────────────────────────
                A REAL CONVERSATION, not a form. For someone signed in there is
                already a messaging system with a thread, history and replies —
                so support is just another chat, and the admin answers it from
                their own Messages like any other.

                Which is why the floating widget is now signed-out only (see
                SupportWidget): a signed-in user has this, and two ways to
                reach support that behave differently is worse than one.

                Pinned to the bottom of the LIST, so it does not scroll away
                with the conversations and is in the same place every time.

                Renders only when an admin account is configured — see
                constants/support.js for why the uid cannot be discovered. */}
            {hasSupportChat(supportUid, user?.uid) && (
              <button
                type="button"
                className="cx-admin-btn"
                onClick={openSupportChat}
                disabled={supportBusy}
              >
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {supportBusy ? t('sup_opening') : t('sup_contact_admin')}
              </button>
            )}
          </aside>
          <section className="msg-thread">
            <ChatThread
              chatId={selectedId}
              other={other}
              onBack={() => setShowThread(false)}
            />
          </section>
        </div>
      </div>
    </div>
  );
}

export function ChatUIProvider({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [chatId, setChatId] = useState('');
  const [wantSupport, setWantSupport] = useState(false);

  const openMessages = useCallback((id = '') => {
    setChatId(id);
    setOpen(true);
  }, []);
  const close = useCallback(() => { setOpen(false); setWantSupport(false); }, []);

  // Opens the overlay already on the admin conversation. Separate from
  // openMessages so a caller cannot accidentally land someone in support by
  // passing the wrong argument.
  const openAdminChat = useCallback(() => { setChatId(''); setWantSupport(true); setOpen(true); }, []);

  // ── ?chat=<id> OPENS THAT CONVERSATION ────────────────────────────────────
  //
  // This is what a tapped push notification arrives on. Conversations are an
  // overlay rather than a route, so there is no URL that renders one directly;
  // the notification links to /dashboard?chat=<id> and this turns that
  // parameter into an open thread.
  //
  // Waits for the user, because the link lands on a protected route: on a cold
  // start the notification opens the app before auth has resolved, and reading
  // the parameter too early would drop it during the redirect through login.
  //
  // Read from window.location rather than useSearchParams: this provider sits
  // ABOVE the router (it wraps the route element), so router hooks are not
  // available here.
  useEffect(() => {
    if (!user?.uid) return;
    const url = new URL(window.location.href);
    const wanted = url.searchParams.get('chat');
    if (!wanted) return;

    openMessages(wanted);

    // Consumed — otherwise the thread reopens on every later render that
    // re-runs this, and the parameter sticks around in anything the user
    // shares or bookmarks.
    url.searchParams.delete('chat');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, [user?.uid, openMessages]);

  // CLOSE ON NAVIGATION. Messages are an overlay, not a route, so it would
  // otherwise stay open on top of whatever page you moved to. On a phone that
  // matters more than it sounds: the ✕ is gone there, and tapping a tab in the
  // bottom bar — which the overlay deliberately leaves visible — is how you
  // leave a conversation.
  //
  // Keyed on pathname only. The `?chat=` deep link above rewrites the query
  // immediately after opening, and reacting to that would close the overlay
  // the instant a push notification opened it.
  const { pathname } = useLocation();
  const firstPath = useRef(pathname);
  useEffect(() => {
    if (pathname === firstPath.current) return;
    firstPath.current = pathname;
    setOpen(false);
  }, [pathname]);

  // Signing out must not leave someone else's conversations on screen.
  useEffect(() => { if (!user?.uid) setOpen(false); }, [user?.uid]);

  return (
    //  is what lets the bottom bar light its Messages tab.
    // The overlay is not a route, so the bar has no pathname to match against.
    <Ctx.Provider value={{ openMessages, openAdminChat, close, messagesOpen: open }}>
      {children}
      {open && user?.uid && <ChatOverlay initialChatId={chatId} openSupport={wantSupport} onClose={close} />}
    </Ctx.Provider>
  );
}
